import { type FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetCustomerEquipmentTypes } from '@views/screens/Shipment/hooks';
import { Form, type FormItemProps, type SelectProps } from 'antd';
import i18n from 'i18next';

import type { RootState } from '@/redux/RootReducer';

import FormSelectBox from '../../../../components/Inputs/FormSelectBox';

interface CustomerEquipmentTypeSelectProps
  extends Pick<FormItemProps, 'rules' | 'name'>,
    Pick<SelectProps, 'placeholder'> {
  label?: string;
  margin?: string;
  editMode?: boolean;
  onChange?: (value: unknown) => void;
  tooltip?: string;
  trailerKey?: number;
  cargoType?: string;
}

export const CustomerEquipmentTypeSelect: FC<CustomerEquipmentTypeSelectProps> = ({
  placeholder = i18n.t('translation:default.fields.equipmentType.placeholder'),
  label = i18n.t('translation:default.fields.equipmentType.label'),
  rules = [],
  name,
  margin,
  editMode,
  onChange,
  tooltip,
  trailerKey,
  cargoType,
}) => {
  const { selectedCompany } = useSelector((state: RootState) => state.auth);
  const { data = [], isLoading } = useGetCustomerEquipmentTypes({
    CargoType: cargoType === '*' ? '**' : (cargoType?.toString() ?? '**'),
    CustomerKey: selectedCompany?.companyId,
    TrailerKey: trailerKey ?? -1,
  });

  const form = Form.useFormInstance();

  const options = useMemo(() => [{ label: 'All', value: -1 }, ...data], [data]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentValue = form.getFieldValue(name as string);
    const optionValues = options.map((opt) => opt.value);

    if (currentValue && !optionValues.includes(currentValue)) {
      form.setFieldsValue({ [name as string]: undefined });
    }
  }, [options, form, name, isLoading]);

  return (
    <FormSelectBox
      loading={isLoading}
      options={options}
      placeholder={placeholder}
      label={label}
      margin={margin}
      rules={rules}
      name={name}
      editMode={editMode}
      onChange={onChange}
      tooltip={tooltip}
    />
  );
};
